import * as React from "react";
import Box from "@mui/material/Box";
import {
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { InputInterface } from "../../../interfaces/InputInterface";
import { LoadingButton } from "@mui/lab";
import PrintIcon from "@mui/icons-material/Print";
import { requestApi } from "../../../helpers/RequestApi";
import { POST } from "../../../utils/MethodUtils";
import { PRINTERS_URL } from "../../../utils/UrlsUtils";
import { toastr } from "react-redux-toastr";
import getErrorApi from "../../../helpers/GetErrorApi";
import { useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

interface State {
  params: any;
  template: string;
  printer: string;
}

interface FormState {
  quantity: InputInterface;
}

const PrintComponent: React.FC<State> = React.memo(
  ({ params, template, printer }) => {
    const maxQuantity = 50;
    const [loading, setLoading] = React.useState<boolean>(false);
    const getDefaultValues = React.useCallback((): FormState => {
      return {
        quantity: {
          value: "1",
          error: "",
        },
      };
    }, []);
    const token = useAppSelector((state: RootState) => state.globalState.token);
    const [values, setValues] = React.useState<FormState>(getDefaultValues());
    const { t } = useTranslation();
    const handleChange = React.useCallback(
      (prop: keyof FormState) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
          setValues((v) => {
            let value = Number(event.target.value);
            if (value <= 0) {
              value = 1;
            }
            if (value > maxQuantity) {
              value = maxQuantity;
            }
            return {
              ...v,
              [prop]: { ...v[prop], value: value.toString(), error: "" },
            };
          });
        },
      []
    );
    const save = React.useCallback(async () => {
      setLoading(true);
      const response = await requestApi({
        method: POST,
        path: PRINTERS_URL + "/print",
        allowError: true,
        token: token,
        body: {
          params: params,
          printer: printer,
          printerTemplate: template,
          quantity: values.quantity.value,
        },
      });
      if (response.statusCode === 200) {
        toastr.success(t("word.success"), t("word.success"));
      } else {
        for (let message of getErrorApi(response.content)) {
          toastr.error(t("word.error"), t(message));
        }
      }
      setLoading(false);
    }, [params, printer, t, template, token, values.quantity.value]);

    let nbChar = values.quantity.value.toString().length;
    if (nbChar < 2) {
      nbChar = 2;
    }

    const addQuantity = React.useCallback(() => {
      setValues((v) => {
        let value = Number(v.quantity.value) + 1;
        if (maxQuantity !== undefined && Number(value) > maxQuantity) {
          value = maxQuantity;
        }
        return {
          ...v,
          quantity: {
            ...v.quantity,
            value: value.toString(),
            error: "",
          },
        };
      });
    }, []);

    const removeQuantity = React.useCallback(() => {
      setValues((v) => {
        let newQuantity = Number(v.quantity.value) - 1;
        if (newQuantity < 1) {
          newQuantity = 1;
        }
        return {
          ...v,
          quantity: {
            ...v.quantity,
            value: newQuantity.toString(),
            error: "",
          },
        };
      });
    }, []);

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {params && (
          <>
            <FormControl variant="outlined" required>
              <InputLabel>{t("field.quantity")}</InputLabel>
              <OutlinedInput
                error={!!values.quantity.error}
                type="number"
                className="center-input"
                value={values.quantity.value}
                onChange={handleChange("quantity")}
                inputProps={{
                  sx: {
                    width: nbChar * 9,
                  },
                }}
                label={t("field.quantity")}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={t("field.action.add_quantity")}
                      onClick={addQuantity}
                      edge="end"
                    >
                      <AddIcon />
                    </IconButton>
                  </InputAdornment>
                }
                startAdornment={
                  <InputAdornment position="start">
                    <IconButton
                      aria-label={t("field.action.remove_quantity")}
                      onClick={removeQuantity}
                      edge="end"
                    >
                      <RemoveIcon />
                    </IconButton>
                  </InputAdornment>
                }
              />
              {!!values.quantity.error && (
                <FormHelperText error>
                  {t(values.quantity.error ?? "")}
                </FormHelperText>
              )}
            </FormControl>
            <LoadingButton
              variant="text"
              color="inherit"
              sx={{
                borderRadius: "50%",
                minWidth: "auto",
                padding: "8px",
                color: "rgba(0, 0, 0, 0.54)",
              }}
              loading={loading}
              onClick={save}
            >
              <PrintIcon />
            </LoadingButton>
          </>
        )}
      </Box>
    );
  }
);

export default PrintComponent;
